import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  ArrowRight, ArrowLeft, CheckCircle2, Rocket, Loader2,
  Globe, FileText, Target, Play, BarChart3, HelpCircle
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BlueprintFlowChart } from '@/components/BlueprintFlowChart';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOALS = [
  { id: 'receptionist', icon: '01', label: 'AI Receptionist', desc: 'Answer calls, qualify leads, book appointments', prompt: 'Create an AI receptionist that answers inbound calls, qualifies leads by asking about project type and timeline, and books appointments on the calendar' },
  { id: 'followup', icon: '02', label: 'Estimate Follow-Up', desc: 'Auto-follow up on unclosed estimates', prompt: 'Create a workflow to automatically follow up on unclosed estimates via email after 48 hours, then phone after 72 hours' },
  { id: 'scheduling', icon: '03', label: 'Smart Scheduler', desc: 'Schedule jobs, avoid conflicts, update CRM', prompt: 'Create a production scheduling workflow that reads the CRM calendar, avoids conflicts, and updates job stages automatically' },
  { id: 'support', icon: '04', label: 'Customer Support', desc: 'Handle tickets, route complex issues', prompt: 'Create a customer support workflow that handles tier-1 tickets automatically and routes complex issues to the team' },
  { id: 'custom', icon: '05', label: 'Custom Workflow', desc: 'Describe your own goal', prompt: '' },
];

const FirstTimeJourney = ({ onComplete }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Data
  const [bizId, setBizId] = useState(null);
  const [bpId, setBpId] = useState(null);
  const [blueprint, setBlueprint] = useState(null);
  const [traceResult, setTraceResult] = useState(null);

  // Step 1
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [industry, setIndustry] = useState('general');
  const [region, setRegion] = useState('');

  // Step 2
  const [notes, setNotes] = useState('');
  const [sopContent, setSopContent] = useState('');

  // Step 3
  const [selectedGoal, setSelectedGoal] = useState('');
  const [customGoal, setCustomGoal] = useState('');

  const handleCreateBusiness = async () => {
    if (!name) return toast.error('Business name required');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/businesses`, { name, website_url: url, industry });
      setBizId(res.data.id);
      await axios.patch(`${API}/businesses/${res.data.id}`, { onboarding_status: 'in_progress' });
      if (url) { try { await axios.post(`${API}/businesses/${res.data.id}/summarize-website`); } catch {} }
      if (region) { try { await axios.patch(`${API}/knowledge/${res.data.id}`, { local_region: { state: region } }); } catch {} }
      setStep(1);
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const handleSaveContext = async () => {
    setLoading(true);
    try {
      if (notes && bizId) await axios.patch(`${API}/knowledge/${bizId}`, { onboarding_notes: notes });
      if (sopContent && bizId) await axios.post(`${API}/knowledge/files/upload`, { business_id: bizId, filename: 'onboarding-sop.txt', content: sopContent, source_type: 'sop' });
      setStep(2);
    } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const handleGenerateWorkflow = async () => {
    const goal = GOALS.find(g => g.id === selectedGoal);
    const prompt = goal?.id === 'custom' ? customGoal : (goal?.prompt || '');
    if (!prompt) return toast.error('Select or describe a goal');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/operator/command`, { mode: 'create', message: prompt, context: { business_id: bizId } });
      setBpId(res.data.blueprint_id);
      // Fetch full blueprint
      const bpRes = await axios.get(`${API}/blueprints/${res.data.blueprint_id}`);
      setBlueprint(bpRes.data);
      setStep(3);
    } catch { toast.error('Generation failed'); }
    finally { setLoading(false); }
  };

  const handleSimulate = async () => {
    if (!bpId) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/blueprints/${bpId}/test`, { test_input: { trigger: 'onboarding_test' } });
      setTraceResult(res.data);
      setStep(4);
    } catch { toast.error('Simulation failed'); }
    finally { setLoading(false); }
  };

  const handleDeploy = async () => {
    if (!bpId) return;
    setLoading(true);
    try {
      await axios.post(`${API}/blueprints/${bpId}/deploy`, {});
      if (bizId) await axios.patch(`${API}/businesses/${bizId}`, { onboarding_status: 'complete' });
      await axios.post(`${API}/auth/complete-onboarding`);
      setStep(5);
    } catch { toast.error('Deploy failed'); }
    finally { setLoading(false); }
  };

  const STEPS = ['Business', 'Context', 'Goal', 'Preview', 'Test', 'Live'];

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="first-time-journey">
      <div className="noise-overlay" />

      {/* Minimal nav */}
      <div className="border-b border-white/5">
        <div className="max-w-3xl mx-auto px-6 h-12 flex items-center justify-between">
          <div className="flex items-center gap-2"><HSLogo size={24} /><span className="font-black text-sm">HumanScaled</span></div>
          <button onClick={() => navigate('/help')} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-white"><HelpCircle className="w-3.5 h-3.5" /> Help</button>
        </div>
      </div>

      {/* Progress */}
      <div className="max-w-3xl mx-auto px-6 pt-6 pb-2">
        <div className="flex items-center gap-0">
          {STEPS.map((s, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div className={`flex-1 h-0.5 ${step >= i ? 'bg-emerald-500' : 'bg-white/10'}`} />}
              <div className={`w-7 h-7 flex items-center justify-center rounded-full text-[10px] font-mono ${step > i ? 'bg-emerald-500 text-black' : step === i ? 'bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/50' : 'bg-white/5 text-muted-foreground'}`}>
                {step > i ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
              </div>
            </React.Fragment>
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground text-center mt-2 font-mono">{STEPS[step]?.toUpperCase()} — STEP {step + 1} OF {STEPS.length}</p>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-6">
        {/* STEP 0: Business */}
        {step === 0 && (
          <div className="space-y-6" data-testid="journey-step-0">
            <div>
              <h1 className="text-2xl font-bold mb-1">Build Your First Revenue-Producing Workflow</h1>
              <p className="text-muted-foreground">Set up your business in 5 minutes. No code required.</p>
            </div>
            <div className="space-y-3">
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Business name" className="bg-[#0a0a0a] border-white/10 rounded-sm h-11" data-testid="j-name" />
              <Input value={url} onChange={e => setUrl(e.target.value)} placeholder="Website URL (optional — we'll summarize it)" className="bg-[#0a0a0a] border-white/10 rounded-sm h-11" data-testid="j-url" />
              <div className="grid grid-cols-2 gap-3">
                <Select value={industry} onValueChange={setIndustry}><SelectTrigger className="bg-[#0a0a0a] border-white/10 rounded-sm h-11"><SelectValue placeholder="Industry" /></SelectTrigger><SelectContent className="bg-[#0a0a0a] border-white/10">
                  {['general', 'construction', 'healthcare', 'insurance', 'realestate', 'legal', 'ecommerce'].map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent></Select>
                <Input value={region} onChange={e => setRegion(e.target.value)} placeholder="State (e.g. TX)" className="bg-[#0a0a0a] border-white/10 rounded-sm h-11" />
              </div>
            </div>
            <Button onClick={handleCreateBusiness} disabled={loading} className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-12" data-testid="j-next-0">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="ml-2 w-4 h-4" /></>}</Button>
          </div>
        )}

        {/* STEP 1: Context */}
        {step === 1 && (
          <div className="space-y-6" data-testid="journey-step-1">
            <div>
              <h1 className="text-2xl font-bold mb-1">Help Your AI Understand the Business</h1>
              <p className="text-muted-foreground">Add context so your workflows are smarter from day one. All optional.</p>
            </div>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Key details: what the business does, how they work, preferences..." className="bg-[#0a0a0a] border-white/10 rounded-sm min-h-[80px]" data-testid="j-notes" />
            <Textarea value={sopContent} onChange={e => setSopContent(e.target.value)} placeholder="Paste any SOP, process doc, or key info (optional)" className="bg-[#0a0a0a] border-white/10 rounded-sm min-h-[60px]" />
            <div className="flex gap-3">
              <Button onClick={() => setStep(0)} variant="outline" className="border-white/10 rounded-sm"><ArrowLeft className="w-4 h-4" /></Button>
              <Button onClick={handleSaveContext} disabled={loading} className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-12" data-testid="j-next-1">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="ml-2 w-4 h-4" /></>}</Button>
            </div>
          </div>
        )}

        {/* STEP 2: Choose Goal */}
        {step === 2 && (
          <div className="space-y-6" data-testid="journey-step-2">
            <div>
              <h1 className="text-2xl font-bold mb-1">What Should Your First Workflow Do?</h1>
              <p className="text-muted-foreground">Pick a goal. We will build the workflow for you.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {GOALS.filter(g => g.id !== 'custom').map(g => (
                <button key={g.id} onClick={() => setSelectedGoal(g.id)} className={`text-left p-4 border transition-all ${selectedGoal === g.id ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-white/5 hover:border-white/10'}`} data-testid={`j-goal-${g.id}`}>
                  <span className="text-xs font-mono text-emerald-400/40 block mb-1">{g.icon}</span>
                  <p className="font-bold text-sm">{g.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{g.desc}</p>
                </button>
              ))}
            </div>
            <button onClick={() => setSelectedGoal('custom')} className={`w-full text-left p-3 border transition-all text-sm ${selectedGoal === 'custom' ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-white/5 hover:border-white/10'}`}>
              <span className="font-bold">Custom Goal</span> — describe your own
            </button>
            {selectedGoal === 'custom' && <Textarea value={customGoal} onChange={e => setCustomGoal(e.target.value)} placeholder="Describe what the workflow should do..." className="bg-[#0a0a0a] border-white/10 rounded-sm min-h-[60px]" data-testid="j-custom" />}
            <div className="flex gap-3">
              <Button onClick={() => setStep(1)} variant="outline" className="border-white/10 rounded-sm"><ArrowLeft className="w-4 h-4" /></Button>
              <Button onClick={handleGenerateWorkflow} disabled={loading || (!selectedGoal && !customGoal)} className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-12" data-testid="j-generate">{loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating...</> : <>Generate Workflow <Rocket className="ml-2 w-4 h-4" /></>}</Button>
            </div>
          </div>
        )}

        {/* STEP 3: Preview */}
        {step === 3 && blueprint && (
          <div className="space-y-6" data-testid="journey-step-3">
            <div>
              <h1 className="text-2xl font-bold mb-1">{blueprint.name}</h1>
              <p className="text-muted-foreground">{blueprint.description || blueprint.summary_text}</p>
            </div>
            <BlueprintFlowChart triggers={blueprint.triggers} states={blueprint.states} outputSchema={blueprint.output_schema} />
            <div className="bg-[#0a0a0a] border border-white/5 p-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div><p className="text-[10px] text-muted-foreground uppercase">States</p><p className="font-mono text-lg font-bold">{blueprint.states?.length || 0}</p></div>
                <div><p className="text-[10px] text-muted-foreground uppercase">Actions</p><p className="font-mono text-lg font-bold">{blueprint.actions?.length || 0}</p></div>
                <div><p className="text-[10px] text-muted-foreground uppercase">Status</p><p className={`font-mono text-lg font-bold ${blueprint.validation?.valid ? 'text-emerald-400' : 'text-amber-400'}`}>{blueprint.validation?.valid ? 'Ready' : 'Review'}</p></div>
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setStep(2)} variant="outline" className="border-white/10 rounded-sm"><ArrowLeft className="w-4 h-4" /></Button>
              <Button onClick={handleSimulate} disabled={loading} className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-12" data-testid="j-simulate">{loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <><Play className="w-4 h-4 mr-2" /></>} Run Simulation</Button>
            </div>
          </div>
        )}

        {/* STEP 4: Simulation Result */}
        {step === 4 && traceResult && (
          <div className="space-y-6" data-testid="journey-step-4">
            <div>
              <h1 className="text-2xl font-bold mb-1">Simulation {traceResult.success ? 'Passed' : 'Complete'}</h1>
              <p className="text-muted-foreground">{traceResult.steps?.length || 0} steps executed in {traceResult.total_duration_ms}ms</p>
            </div>
            <div className="bg-[#0a0a0a] border border-white/5 p-4 space-y-2">
              {(traceResult.steps || []).map((s, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <span className={`w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-mono ${s.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>{s.step || i + 1}</span>
                  <span className="flex-1">{s.action}</span>
                  <span className="text-xs text-muted-foreground font-mono">{s.duration_ms}ms</span>
                </div>
              ))}
            </div>
            <div className="bg-emerald-500/5 border border-emerald-500/20 p-4 text-center">
              <p className="text-sm text-emerald-400 font-medium">Ready to deploy in shadow mode</p>
              <p className="text-xs text-muted-foreground mt-1">Shadow mode executes fully but does not send emails, make calls, or write to CRM. Safe to test.</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setStep(3)} variant="outline" className="border-white/10 rounded-sm"><ArrowLeft className="w-4 h-4" /></Button>
              <Button onClick={handleDeploy} disabled={loading} className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-12" data-testid="j-deploy">{loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <><Rocket className="w-4 h-4 mr-2" /></>} Deploy (Shadow Mode)</Button>
            </div>
          </div>
        )}

        {/* STEP 5: Complete */}
        {step === 5 && (
          <div className="text-center space-y-6 py-8" data-testid="journey-step-5">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto"><CheckCircle2 className="w-8 h-8 text-emerald-400" /></div>
            <h1 className="text-2xl font-bold">Your First Workflow is Live</h1>
            <p className="text-muted-foreground max-w-md mx-auto">Running in shadow mode. Monitor it, review traces, and switch to live when you are confident.</p>
            <div className="flex justify-center gap-3">
              <Button onClick={() => { onComplete?.(); navigate('/agents'); }} className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-11"><BarChart3 className="w-4 h-4 mr-2" /> Monitor Agent</Button>
              <Button onClick={() => { onComplete?.(); navigate('/dashboard'); }} variant="outline" className="border-white/10 rounded-sm h-11">Go to Dashboard</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FirstTimeJourney;
